import React, { FunctionComponent } from 'react'
import styled from 'styled-components'
import { useNavigate } from 'react-router-dom'
import { styles } from '../../../styles/themes/style'
import { AppRoutes } from '../../../interfaces/store/appStore'
import { useAppActions } from '../../../stores/application/useAppActions'
import { useAppStore } from '../../../stores/application/useAppStore'
import { useAuthStore } from '../../../stores/auth/useAuthStore'
import OnboardingStepLayout from '../components/OnboardingStepLayout'
import OnboardingSuccessIcon from '../components/OnboardingSuccessIcon'
import { clearSkippedToAllSet } from '../onboardingExtensionSkipUtils'
import { hasConnectedLinkedInProfile } from '../linkedinConnectFlowUtils'

const SYNC_IN_PROGRESS_MESSAGE =
    "We're syncing your LinkedIn network now. Your connections will start appearing in your workspace within a few minutes."

const SYNC_IN_APP_MESSAGE =
    'Your workspace is ready. When you want to import your network, sync your LinkedIn connections from Home or Contacts in the app.'

const READY_WITH_EXTENSION_MESSAGE =
    'Your workspace is ready. Connect your LinkedIn profile and sync your connections anytime from the app.'

const NO_EXTENSION_MESSAGE =
    'Your workspace is ready, and you can start exploring LeadDelta. To bring in your LinkedIn contacts, install the Chrome extension.'

const AllSetSection: FunctionComponent = () => {
    const { setOnboardingStep } = useAppActions()
    const { store: { extensionInstalled } } = useAppStore()
    const { store: { user } } = useAuthStore()
    const navigate = useNavigate()

    const messageText = (() => {
        if (!extensionInstalled) return NO_EXTENSION_MESSAGE
        if (user?.isSynced) return SYNC_IN_PROGRESS_MESSAGE
        if (hasConnectedLinkedInProfile(user)) return SYNC_IN_APP_MESSAGE
        return READY_WITH_EXTENSION_MESSAGE
    })()

    const goToWorkspace = () => {
        clearSkippedToAllSet()
        setOnboardingStep(-2)
        navigate(AppRoutes.HOME)
    }

    return (
        <OnboardingStepLayout activeStepIndex={4} title="You're all set" showStepper={Boolean(extensionInstalled)}>
            <AllSetCard>
                <IconWrap>
                    <OnboardingSuccessIcon />
                </IconWrap>
                <MessageText>{messageText}</MessageText>
                <PrimaryButton type="button" onClick={goToWorkspace}>
                    Go to my workspace
                </PrimaryButton>
            </AllSetCard>
        </OnboardingStepLayout>
    )
}

export default AllSetSection

const AllSetCard = styled.div`
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 32px 24px 24px;
    gap: 32px;
    width: 100%;
    border: 1px solid ${styles.colors.black200};
    border-radius: 12px;
    background: ${styles.colors.white};
`

const IconWrap = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 120px;
    height: 120px;
`

const MessageText = styled.p`
    margin: 0;
    width: 100%;
    font-family: NunitoSans;
    font-weight: 400;
    font-size: 18px;
    line-height: 24px;
    text-align: center;
    color: ${styles.colors.black400};
`

const PrimaryButton = styled.button`
    box-sizing: border-box;
    width: 100%;
    min-height: 52px;
    padding: 0 24px;
    border-radius: 12px;
    border: 1px solid ${styles.colors.primary600};
    background-color: ${styles.colors.primary600};
    color: ${styles.colors.white};
    font-family: NunitoSans;
    font-weight: 700;
    font-size: 14px;
    line-height: 20px;
    cursor: pointer;
    transition: background-color 0.2s ease, border-color 0.2s ease;

    &:hover {
        background-color: ${styles.colors.primary400};
        border-color: ${styles.colors.primary400};
    }

    &:focus {
        outline: none;
    }
`
